import { OpsInstruction, WeatherEvent } from 'app/model/SOL49';
import React, { ReactNode } from 'react';
import { ManagedColumn } from 'app/common/table/ManagedTable';
import { WeatherEventToolbar } from 'app/components/dashboard/global/events/MonitoredEventToolbar';
import KeyUtils from 'app/common/util/KeyUtils';
import WeatherEventTable, { WeatherEventColumns } from 'app/components/panels/predict/weather/WeatherEventTable';
import { WeatherEventForm } from 'app/components/panels/predict/weather/WeatherEventForm';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { notification } from 'antd';
import { TvRefLocation } from 'app/model/CORE';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import Utils from 'app/common/util/Utils';
import {
    AbstractMonitoredEventPanel,
    AbstractMonitoredEventPanelProps,
    AbstractMonitoredEventPanelState,
} from 'app/components/panels/predict/AbstractMonitoredEventPanel';

export interface WeatherEventPanelProps extends AbstractMonitoredEventPanelProps<WeatherEvent> {
    refLocs: TvRefLocation[];
    defaultRefLoc?: TvRefLocation;
    opsInstructions: OpsInstruction[];
}

interface WeatherEventPanelState extends AbstractMonitoredEventPanelState<WeatherEvent> {
    textFilter: string;
}

export class WeatherEventPanel extends AbstractMonitoredEventPanel<
    WeatherEvent,
    WeatherEventPanelProps,
    WeatherEventPanelState
> {
    protected columns: ManagedColumn<WeatherEvent>[] = WeatherEventColumns;

    protected getToolbar(): ReactNode {
        return (
            <WeatherEventToolbar
                showTags={true}
                onAddEvent={() => this.setState({ selected: undefined })}
                settings={this.props.settings}
                onSettings={this.onSettingsChange}
                textFilter={this.state.textFilter}
                filters={this.props.filters}
                onFilter={this.onFiltersChange}
                onTextFilterChange={filter => this.setState({ textFilter: filter.toUpperCase() })}
                disabled={Utils.isNotExists(this.state.selected)}
            />
        );
    }

    protected getDefaultEvent(): WeatherEvent {
        return {
            refLocs: this.props.defaultRefLoc && [this.props.defaultRefLoc],
        };
    }

    protected getForm(event: WeatherEvent): React.ReactNode {
        return (
            <WeatherEventForm
                event={event}
                refLocs={this.props.refLocs}
                defaultRefLoc={this.props.defaultRefLoc}
                filters={this.props.filters}
                opsInstructions={this.props.opsInstructions}
                onCreate={this.onCreate}
                onUpdate={this.onUpdate}
                onDelete={this.onDelete}
                onWidthChange={this.props.onWidthChange}
            />
        );
    }

    protected getTable(events: WeatherEvent[], selected: WeatherEvent): React.ReactNode {
        return (
            <WeatherEventTable
                highlight={this.highlight}
                data={events}
                columns={WeatherEventColumns}
                keyProvider={KeyUtils.MONITORED_EVENT_KEY_PROVIDER}
                noDataMessage={'No events'}
                selection={this.selection(selected)}
            />
        );
    }

    protected filterEvents(events: WeatherEvent[]): WeatherEvent[] {
        events = super.filterEvents(events);
        events = Utils.isEmptyString(this.state.textFilter)
            ? events
            : events.filter(e => e.title.toUpperCase().includes(this.state.textFilter));
        return Utils.isExists(this.props.filters)
            ? events.filter(event => this.props.filters.types.includes(event.type))
            : events;
    }

    protected refresh = () => {
        ServiceFactory.sol49Service()
            .getMonitoredEvents({ refLocation: this.props.defaultRefLoc })
            .then(reply => {
                this.setState({ events: reply.data.filter(MonitoredEventUtils.isWeatherEvent) }, () => {
                    if (this.props.filters) {
                        const filterTypes = Array.from(new Set(this.state.events.map(event => event.type)));
                        const newTypes = filterTypes.filter(type => !this.props.filters.options.includes(type));
                        this.onFiltersChange({
                            types: this.props.filters.types.concat(newTypes),
                            options: filterTypes,
                        });
                    }
                });
            })
            .catch(err => notification.error({ message: 'Error fetching events', description: '' + err }))
            .finally(() => this.setState({ loading: false }));
    };
}
